import { ECommerceProductExternalProvider } from '../../components-new/eCommerce/helpers/data';

export enum eCommerceTheme {
  light = 'light',
  dark = 'dark',
}

export enum MediaType {
  IMAGE = 'image',
  YOUTUBE = 'youtube',
  HEXA3D = 'hexa3d',
}

export enum ProductStatus {
  ACTIVE = 'Active',
  ARCHIVED = 'Archived',
  DRAFT = 'Draft',
}

enum OpticalCenterTypes {
  LUNETTES = 'Lunettes',
  LUNETTES_SOLEIL = 'Lunettes soleil',
}

export interface IProduct {
  id?: number;
  title: string;
  previewImage?: { url: string };
  userId: number;
  tourId: number;
  description: string;
  countryId: number;
  weight: number;
  weightUnit: string;
  isTrackQuantity?: boolean;
  quantity?: number;
  sellingOutOfStock?: boolean;
  barcode: string;
  sku: string;
  price: number;
  comparePrice: number;
  isShipping: boolean;
  hsCode: string;
  warrantyFile: string;
  specificationsFile: string;
  eCommerceVendorId: number;
  eCommerceProductTypeId: number;
  eCommerceExternalProductTypeId: number;
  eCommerceExternalProvider: string;
  isMultipleOptions: boolean;
  externalProductId: string;
  icon?: string;
  iconFile?: string;
  opacity?: number;
  iconColor?: string;
  sid: string;
  positionX: number;
  positionY: number;
  positionZ: number;
  stemVectorX: number;
  stemVectorY: number;
  stemVectorZ: number;
  media?: any[];
  specifications: any[];
  options?: any[];
  variants?: any[];
  status: ProductStatus;
  vat: number;
  tags: any[];
}

const getDefaultPrice = (data: any[] = []) => {
  if (!data.length) return null;
  const defaultVariant = data.find((item) => item.isDefault);
  return defaultVariant?.price || null;
};

export class Product implements IProduct {
  id?: number;

  title: string;

  tourId: number;

  userId: number;

  description: string;

  countryId: number;

  status: ProductStatus;

  weight: number;

  weightUnit: string;

  isTrackQuantity?: boolean;

  quantity?: number;

  sellingOutOfStock?: boolean;

  barcode: string;

  sku: string;

  price: number;

  comparePrice: number;

  isShipping: boolean;

  hsCode: string;

  warrantyFile: string;

  specificationsFile: string;

  eCommerceVendorId: number;

  eCommerceProductTypeId: number;

  eCommerceExternalProductTypeId: number;

  eCommerceExternalProvider: string;

  externalProductId: string;

  isMultipleOptions: boolean;

  icon?: string;

  iconFile?: string;

  opacity?: number;

  iconColor?: string;

  sid: string;

  positionX: number;

  positionY: number;

  positionZ: number;

  stemVectorX: number;

  stemVectorY: number;

  stemVectorZ: number;

  media?: any[];

  specifications: any[];

  options?: any[];

  variants?: any[];

  previewImage?: { url: string };

  isEnableCart: boolean;

  cartLink: string;

  cartLinkType: string;

  vat: number;

  tags: any[];

  productType?: string;

  vendor?: string;

  constructor(model: any = {}, userId) {
    this.id = model.id || null;
    this.tourId = model.tourId || null;
    this.title = model.title || null;
    this.previewImage = model.previewImage || null;
    this.userId = userId || null;
    this.description = model.description || null;
    this.variants = model.variants || [];
    const customVariant = this.getMinVariant(model.variants, 'price');
    const defaultPrice = getDefaultPrice(model.variants);

    this.countryId = customVariant?.countryId || null;
    // this.status = customVariant?.status || null;
    this.weight = customVariant?.weight || null;
    this.weightUnit = customVariant?.weightUnit || null;
    this.isTrackQuantity = customVariant?.isTrackQuantity || false;
    this.quantity = customVariant?.quantity || 0;
    this.sellingOutOfStock = customVariant?.sellingOutOfStock || false;
    this.barcode = customVariant?.barcode || null;
    this.price = defaultPrice || customVariant?.price || null;
    this.comparePrice = customVariant?.comparePrice || null;
    this.isShipping = customVariant?.isShipping || false;
    this.hsCode = customVariant?.hsCode || null;

    this.warrantyFile = model.warrantyFile || null;
    this.specificationsFile = model.specificationsFile || null;
    this.eCommerceVendorId = model.eCommerceVendorId || null;
    this.eCommerceProductTypeId = model.eCommerceProductTypeId || null;
    this.eCommerceExternalProductTypeId = this.setECommerceExternalProductTypeId(
      model.productType,
      model.externalProvider,
    );
    this.externalProductId = model.externalProductId || null;
    this.eCommerceExternalProvider = model.externalProvider || null;
    this.isMultipleOptions = model.isMultipleOptions || false;
    this.icon = model.icon || null;
    if (model.icon && model.icon?.indexOf('.') !== -1) {
      this.iconFile = `${process.env.NEXT_PUBLIC_CDN}/users/${this.userId}/eCommerce/${this.id}/${model.icon}`;
      this.icon = null;
    }
    this.opacity = model.opacity || 0;
    this.iconColor = model.iconColor || null;
    this.media =
      model.media?.map((item: any) => ({
        ...item,
        url: this.getMediaUrl(item.url, item.type),
        embed: item.type === MediaType.YOUTUBE ? item.url : '',
      })) || [];
    this.media = this.saveMediaByUrl(this.media);

    this.specifications = model.specifications || [];
    this.options = model.options || [];

    this.previewImage =
      this.media.find(
        (item) => item.type !== MediaType.YOUTUBE && item.type !== MediaType.HEXA3D,
      ) || {};
    this.sid = model.sid || null;
    this.positionX = model.positionX || null;
    this.positionY = model.positionY || null;
    this.positionZ = model.positionZ || null;
    this.stemVectorX = model.stemVectorX || null;
    this.stemVectorZ = model.stemVectorZ || null;
    this.stemVectorY = model.stemVectorY || null;
    this.status = model.status || null;

    this.isEnableCart = model.isEnableCart ?? false;
    this.cartLink = model.cartLink || '#';
    this.cartLinkType = model.cartLinkType || null;
    this.vat = model.vat ?? 0;
    this.tags = model?.tags || [];
    this.productType = model?.productType?.name || null;
    this.vendor = model?.vendor?.name || null;
  }

  getMediaUrl(url: string, type: string) {
    if (type === MediaType.YOUTUBE) {
      return `https://img.youtube.com/vi/${url}/sddefault.jpg`;
    }
    if (type === MediaType.HEXA3D) {
      if (url.includes('static.fittingbox.com') && url.includes('sku')) {
        const arrayOfLinkParts = url.split('=');
        if (!arrayOfLinkParts[arrayOfLinkParts.length - 1]) {
          return null;
        }
        return url;
      }
      return url;
    }
    return `${process.env.NEXT_PUBLIC_CDN}/users/${this.userId}/eCommerce/${this.id}/media/${url}`;
  }

  // eslint-disable-next-line class-methods-use-this
  saveMediaByUrl(media: any[]) {
    const havellsTypeIndex = media?.findIndex((item) => item?.type === MediaType.HEXA3D);
    return media[havellsTypeIndex]?.url === null
      ? media.filter((_, index) => index !== havellsTypeIndex)
      : media;
  }

  // eslint-disable-next-line class-methods-use-this
  getMinVariant(data: any[] = [], field: string) {
    if (!data.length) return null;
    const filtered = data.filter(
      (item) => (data.length > 1 && !item.isDefault) || data.length === 1,
    );
    return filtered.reduce(
      (minItem, item) => (item[field] != null && minItem[field] > item[field] ? item : minItem),
      filtered[0],
    );
  }

  // eslint-disable-next-line consistent-return,class-methods-use-this
  setECommerceExternalProductTypeId(productType, externalProvider) {
    if (productType?.externalTypeId) {
      return productType?.externalTypeId;
    }
    if (externalProvider === ECommerceProductExternalProvider.OPTICAL_CENTER) {
      return productType?.name === OpticalCenterTypes.LUNETTES ? 1 : 2;
    }
    return null;
  }
}

export type Variant = {
  barcode: null;
  colorOptionId: number;
  comparePrice: number;
  countryId: number;
  createdAt: string;
  eCommerceProductId: number;
  externalVariantId: null;
  hsCode: null;
  id: number;
  isDefault: false;
  isShipping: true;
  isTrackQuantity: false;
  materialOptionId: number;
  mediaId: number;
  price: number;
  quantity: number;
  sellingOutOfStock: false;
  sizeOptionId: number;
  sku?: number;
  updatedAt: string;
  weight: number;
  weightUnit: string;
  colorOption: {
    id: number;
    externalOptionId?: null;
    value: string;
    createdAt: string;
    updatedAt: string;
  };
  sizeOption: {
    id: number;
    externalOptionId: null;
    value: string;
    eCommerceOptionId: number;
    eCommerceProductId: number;
  };
  media: {
    id: number;
    externalMediaId: null;
    eCommerceProductId: number;
    url: string;
    type: string;
  };
  materialOption: {
    id: number;
    externalOptionId?: number;
    value: string;
    createdAt: string;
    updatedAt: string;
  };
};

export enum EXTERNAL_PROVIDER {
  SHOPIFY = 'shopify',
  OPTICAL_CENTER = 'opticalCenter',
  HAVELLS = 'havells',
}

export type Settings = {
  id?: number;

  // new fields
  shopInfo?: string;
  shopName?: string;
  shopAddress?: string;

  shopDescription?: string;

  activeReel: string;
  contactMeIframeLink: string;
  contactMeOn: boolean;
  contactMeRequiredFields: any[];
  contactMeType: string;
  currency: { id: number; name: string; code: string; symbol: string };
  havellsApi: boolean;
  isOpenReel: boolean;
  isStripePaymentEnabled: boolean;
  payNowOn: boolean;
  selfPickupOn: boolean;
  shippingOn: boolean;
  stripeClientApiKey?: string;
  theme?: eCommerceTheme;
  externalProvider: EXTERNAL_PROVIDER;
  masterTourId: number | null;
};
